import { useCallback, useEffect, useState } from "react";
import api from "../api";

// Shared BUSINESS/DATA logic for the Developer / Data Analyst home
// dashboards. TWO DISTINCT, never-mixed data sources, both self-scoped and
// both split by task_type (never by assignment direction):
//   - "My Tickets"    -> GET /my-tickets    (created_by = me AND task_type
//     <> IT_SUPPORT) -- the caller's own professional work log.
//   - "My Complaints" -> GET /my-complaints (created_by = me AND task_type
//     = IT_SUPPORT) -- IT-support complaints the caller raised.
// See services/taskAssignmentService.js#listMyTickets/listMyComplaints.
// Plus the existing self-scoped GET /my-form-assignments
// (assigned_forms:view_own) for Review Forms. Nothing here fetches
// org-wide data or filters an unscoped list in the browser, and nothing
// here uses assigned_to (that is an entirely different, unrelated concept
// from "tickets/complaints I raised").
const RECENT_LIMIT = 8;
// Full-list fetch for client-side analytics (Data Analyst dashboard) --
// same pageSize:200 convention ComplaintsListPage.jsx already uses for
// self-scoped "my-*" lists (buildPagination clamps to <=200 server-side).
// Additive only: existing recent/counts fields above are untouched, so
// DeveloperDashboard.jsx (which only reads those) is unaffected.
const ALL_LIMIT = 200;

const emptyCounts = { total: 0, pending: 0, in_progress: 0, completed: 0 };

function fetchAllList(endpoint) {
  return api.get(endpoint, { params: { page: 1, pageSize: ALL_LIMIT } })
    .then((res) => (Array.isArray(res.data?.data) ? res.data.data : []));
}

function fetchCountedList(endpoint) {
  return Promise.all([
    api.get(endpoint, { params: { page: 1, pageSize: RECENT_LIMIT } }),
    api.get(endpoint, { params: { status: "pending", page: 1, pageSize: 1 } }),
    api.get(endpoint, { params: { status: "in_progress", page: 1, pageSize: 1 } }),
    api.get(endpoint, { params: { status: "completed", page: 1, pageSize: 1 } }),
  ]).then(([recentRes, pendingRes, inProgressRes, completedRes]) => ({
    recent: Array.isArray(recentRes.data?.data) ? recentRes.data.data : [],
    counts: {
      total: recentRes.data?.meta?.total || 0,
      pending: pendingRes.data?.meta?.total || 0,
      in_progress: inProgressRes.data?.meta?.total || 0,
      completed: completedRes.data?.meta?.total || 0,
    },
  }));
}

export default function useMyTicketsDashboard() {
  const [recentTickets, setRecentTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState(emptyCounts);
  const [ticketsLoading, setTicketsLoading] = useState(false);

  const [recentComplaints, setRecentComplaints] = useState([]);
  const [complaintCounts, setComplaintCounts] = useState(emptyCounts);
  const [complaintsLoading, setComplaintsLoading] = useState(false);

  // Full lists (up to 200 rows each) -- consumed by DataAnalystDashboard.jsx
  // for client-side analytics; not read by DeveloperDashboard.jsx.
  const [allTickets, setAllTickets] = useState([]);
  const [allComplaints, setAllComplaints] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allError, setAllError] = useState(false);

  const [reviewFormsCount, setReviewFormsCount] = useState(0);
  const [reviewFormsLoading, setReviewFormsLoading] = useState(false);
  const [reviewFormsAvailable, setReviewFormsAvailable] = useState(true);

  const fetchTickets = useCallback(async () => {
    setTicketsLoading(true);
    try {
      const { recent, counts } = await fetchCountedList("/my-tickets");
      setRecentTickets(recent);
      setTicketCounts(counts);
    } catch (err) {
      console.error("Fetch my tickets error:", err);
      setRecentTickets([]);
      setTicketCounts(emptyCounts);
    } finally {
      setTicketsLoading(false);
    }
  }, []);

  const fetchComplaints = useCallback(async () => {
    setComplaintsLoading(true);
    try {
      const { recent, counts } = await fetchCountedList("/my-complaints");
      setRecentComplaints(recent);
      setComplaintCounts(counts);
    } catch (err) {
      console.error("Fetch my complaints error:", err);
      setRecentComplaints([]);
      setComplaintCounts(emptyCounts);
    } finally {
      setComplaintsLoading(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setAllLoading(true);
    setAllError(false);
    const [ticketsRes, complaintsRes] = await Promise.allSettled([
      fetchAllList("/my-tickets"),
      fetchAllList("/my-complaints"),
    ]);
    if (ticketsRes.status === "fulfilled") setAllTickets(ticketsRes.value);
    else { setAllTickets([]); console.error("Fetch all tickets error:", ticketsRes.reason); }
    if (complaintsRes.status === "fulfilled") setAllComplaints(complaintsRes.value);
    else { setAllComplaints([]); console.error("Fetch all complaints error:", complaintsRes.reason); }
    // Partial-data signal: exactly one side failed while the other
    // succeeded -- the dashboard should say so rather than silently
    // analyzing half the data. If BOTH fail, both lists are simply empty
    // and the existing empty-state handling already covers that (matches
    // how fetchTickets/fetchComplaints above already swallow errors).
    setAllError((ticketsRes.status === "rejected") !== (complaintsRes.status === "rejected"));
    setAllLoading(false);
  }, []);

  const fetchReviewFormsCount = useCallback(async () => {
    setReviewFormsLoading(true);
    try {
      const res = await api.get("/my-form-assignments", { params: { page: 1, pageSize: 1 } });
      setReviewFormsCount(res.data?.meta?.total || 0);
      setReviewFormsAvailable(true);
    } catch (err) {
      // Not approved / not yet granted for this role, or no assignments —
      // either way, the dashboard should degrade to its empty state rather
      // than surface an error.
      setReviewFormsCount(0);
      setReviewFormsAvailable(false);
    } finally {
      setReviewFormsLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    fetchTickets();
    fetchComplaints();
    fetchAll();
    fetchReviewFormsCount();
  }, [fetchTickets, fetchComplaints, fetchAll, fetchReviewFormsCount]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    // My Tickets (created_by = me, task_type <> IT_SUPPORT)
    recentTickets,
    ticketCounts,
    ticketsLoading,
    // My Complaints (created_by = me, task_type = IT_SUPPORT)
    recentComplaints,
    complaintCounts,
    complaintsLoading,
    // Full lists (up to 200 rows) for client-side analytics
    allTickets,
    allComplaints,
    allLoading,
    allError,
    // Review Forms
    reviewFormsCount,
    reviewFormsLoading,
    reviewFormsAvailable,
    refresh,
  };
}
